/**
 * compare.js — side-by-side plan comparison (spec 2026-07-12, rev 2 per owner feedback).
 *
 * Ported from archive/quoting-enterprise-pass-final (27bcba5) and extended: per-card "Compare"
 * toggles (max 4, cart-independent) SNAPSHOT the card's current variant/riders/premium into a
 * session-scoped store; the dedicated compare/ page renders purely from the snapshot. Pure logic +
 * renderer live here; DOM wiring in script.js (toggle + pill + CMS detail merge + doctor/Rx
 * coverage attach) and compare/compare.js (page hydration).
 *
 * Rev 2 (owner feedback 2026-07-12): fixed 11-row copay section (CMS display_string first, text
 * benefits keyword-matched, fallback "Excluded" / "Network discount…" per product type); network
 * cell links to the provider directory; Documents = brochure only; key-benefits KEPT in the column
 * model but NOT rendered (pinned for the consumer site); doctor/Rx in-network rows when captured.
 */
import { getOopMax, getProductType, getPolicyTermLabel } from './plan-classify.js';
import { getAgentNpn, getCachedProfile, toBranding, authHeaders } from './agent-identity.js';

export const MAX_COMPARE = 4;
const STORE_KEY = 'wqCompareSelection';

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Toggle a plan id in the comparison selection.
 * @returns {{ids: string[], added: boolean, removed: boolean, rejected: boolean}}
 */
export function togglePlanId(ids, planId, max = MAX_COMPARE) {
  const id = String(planId);
  const list = (ids || []).map(String);
  const idx = list.indexOf(id);
  if (idx !== -1) {
    list.splice(idx, 1);
    return { ids: list, added: false, removed: true, rejected: false };
  }
  if (list.length >= max) {
    return { ids: list, added: false, removed: false, rejected: true };
  }
  list.push(id);
  return { ids: list, added: true, removed: false, rejected: false };
}

// ---------------------------------------------------------------------------
// Copay extraction (rev 2 #4)
// ---------------------------------------------------------------------------

export const NETWORK_DISCOUNT = 'Network discount — subject to deductible and coinsurance';

/** The fixed copay rows, in owner-specified order. */
export const COPAY_CATEGORIES = [
  { key: 'pcp', label: 'Primary care visit' },
  { key: 'specialist', label: 'Specialist visit' },
  { key: 'urgentCare', label: 'Urgent care' },
  { key: 'genericRx', label: 'Generic Rx' },
  { key: 'brandRx', label: 'Brand Rx' },
  { key: 'labwork', label: 'Labwork' },
  { key: 'preventive', label: 'Preventive care' },
  { key: 'xrays', label: 'X-rays' },
  { key: 'imaging', label: 'Diagnostic imaging (CT/MRI)' },
  { key: 'mentalHealth', label: 'Mental health (outpatient)' },
  { key: 'telehealth', label: 'Telehealth' },
];

// CMS Marketplace benefit `type` constants per category (detail response carries the full list;
// the search response only a subset — script.js merges the detail at snapshot time).
const CMS_BENEFIT_TYPES = {
  pcp: 'PRIMARY_CARE_VISIT_TO_TREAT_AN_INJURY_OR_ILLNESS',
  specialist: 'SPECIALIST_VISIT',
  urgentCare: 'URGENT_CARE_CENTERS_OR_FACILITIES',
  genericRx: 'GENERIC_DRUGS',
  brandRx: 'PREFERRED_BRAND_DRUGS',
  labwork: 'LABORATORY_OUTPATIENT_AND_PROFESSIONAL_SERVICES',
  preventive: 'PREVENTIVE_CARE_SCREENING_IMMUNIZATION',
  xrays: 'X_RAYS_AND_DIAGNOSTIC_IMAGING',
  imaging: 'IMAGING_CT_PET_SCANS_MRIS',
  mentalHealth: 'MENTAL_BEHAVIORAL_HEALTH_OUTPATIENT_SERVICES',
  telehealth: 'TELEHEALTH',
};

// Keyword matcher for carriers whose benefits are text rows (NGAH/UHOne/…). NGAH also uses
// terse row names: OV (office visits), RxCoverage.
const CATEGORY_RE = {
  pcp: /primary care|doctor visit|office visit|physician visit|^ov$/i,
  specialist: /specialist/i,
  urgentCare: /urgent care/i,
  genericRx: /generic|rx ?coverage/i,
  brandRx: /brand|rx ?coverage/i,
  labwork: /\blabs?\b|laborator/i,
  preventive: /preventive|wellness/i,
  xrays: /x-?rays?/i,
  imaging: /imaging|mri|ct scan|pet scan/i,
  mentalHealth: /mental|behavioral/i,
  telehealth: /telehealth|telemed|virtual/i,
};

// Slugs as plan-classify's getProductType actually returns them. Gap/expense-shield products are
// deliberately NOT medical here: they pay fixed benefits — "network discount" wording would be
// inaccurate (owner bug report 2026-07-12), so their absent categories read "Excluded". NGAH types
// Medical Expense Shield as STM, so the name check below catches it without moving its
// results-page filter bucket.
const MEDICAL_TYPES = new Set(['aca', 'stm', 'major-medical', 'limited-medical']);
const GAP_NAME_RE = /expense shield/i;

// In-network cost share for one CMS benefit; CMS's display_string is authoritative
// ("$25", "No Charge After Deductible", "30% Coinsurance after deductible").
function cmsShare(benefit) {
  if (!benefit) return null;
  if (benefit.covered === false) return 'Excluded';
  const list = Array.isArray(benefit.cost_sharings) ? benefit.cost_sharings : [];
  const cs = list.find((c) => c && c.network_tier === 'In-Network') || list[0];
  if (!cs) return null;
  if (cs.display_string) return String(cs.display_string);
  if (Number(cs.copay_amount) > 0) return '$' + cs.copay_amount + ' copay';
  return null;
}

/**
 * Fixed 11-slot copay map for a plan. `detailBenefits` (CMS plan-detail benefits list) takes
 * precedence over the search-response list when provided. Fallback per owner rule: a slot with
 * no data reads "Network discount — subject to deductible and coinsurance" on medical products,
 * "Excluded" on non-medical ones.
 * @returns {Object<string,string>}
 */
export function extractCopays(plan, detailBenefits) {
  const out = {};
  const cmsBenefits = Array.isArray(detailBenefits) && detailBenefits.length
    ? detailBenefits
    : (plan && plan._cmsData && Array.isArray(plan._cmsData.benefits) ? plan._cmsData.benefits : null);

  if (cmsBenefits) {
    for (const cat of COPAY_CATEGORIES) {
      const b = cmsBenefits.find((x) => x && x.type === CMS_BENEFIT_TYPES[cat.key]);
      out[cat.key] = cmsShare(b);
    }
  } else {
    const rows = (plan && plan._apiData && Array.isArray(plan._apiData.benefits))
      ? plan._apiData.benefits
      : (plan && plan.planDetails && Array.isArray(plan.planDetails.benefits) ? plan.planDetails.benefits : []);
    for (const cat of COPAY_CATEGORIES) {
      const m = rows.find((r) => r && r.name && CATEGORY_RE[cat.key].test(String(r.name)));
      let v = m ? (String(m.formattedValue || m.value || '').trim() || null) : null;
      // Zero semantics differ (owner bug report 2026-07-12): a bare "0" is a FLAG — no benefit
      // (NGAH: RxCoverage=0, Wellness=0, OV=0) → Excluded. A dollar "$0"/"$0.00" is a copay
      // amount → covered at no cost.
      if (v && /^0$/.test(v)) v = 'Excluded';
      else if (v && /^\$0(\.00)?$/.test(v)) v = 'No charge';
      out[cat.key] = v;
    }
  }

  // Medical Expense Shield: curated from the official brochure (AHS_MES_2142-1 Rev. 06/2026,
  // pages 8-9 + exclusions) — the API's zero flags are ambiguous (OV=0 means "no copay" while
  // RxCoverage=0 means "excluded"). Covered services accrue at network rates and pay 100% after
  // the deductible; outpatient Rx and mental health are policy exclusions. Same curated-brochure
  // precedent as the Manhattan DVH benefits in script.js.
  const isGapProduct = GAP_NAME_RE.test(String((plan && plan.name) || ''));
  if (isGapProduct) {
    const SUBJ = 'Subject to deductible — plan pays 100% after';
    return {
      pcp: SUBJ, specialist: SUBJ, urgentCare: SUBJ, labwork: SUBJ, preventive: SUBJ,
      xrays: SUBJ, imaging: SUBJ, telehealth: SUBJ,
      genericRx: 'Excluded', brandRx: 'Excluded', mentalHealth: 'Excluded',
    };
  }
  const fallback = MEDICAL_TYPES.has(getProductType(plan || {})) ? NETWORK_DISCOUNT : 'Excluded';
  for (const cat of COPAY_CATEGORIES) {
    if (!out[cat.key]) out[cat.key] = fallback;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Column extraction
// ---------------------------------------------------------------------------

// Delegates to plan-classify's getPolicyTermLabel (2026-09-10 review finding) instead of
// duplicating its logic with stale hardcoded UHOne terms ('36 months' / '3-12 months') that no
// longer match the Rate Engine's real per-state coverageMonthsStart/End data — this function was
// the compare page's and the comparison email's ONLY source for the term column/row, so it was
// mislabeling every UHOne row (e.g. a TX/NC 4-month plan showing "3-12 months").
function defaultTermLabel(plan) {
  return getPolicyTermLabel(plan);
}

function metalTierOf(plan) {
  const raw = plan.metalLevel || (plan.planDetails && plan.planDetails.metallicCode) || '';
  const tier = String(raw).trim();
  if (!tier) return null;
  return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
}

function coinsuranceOf(plan) {
  if (plan.coinsurance != null) return String(plan.coinsurance);
  if (plan.planDetails && plan.planDetails.coinsurance != null) {
    return String(plan.planDetails.coinsurance);
  }
  const match = String(plan.name || '').match(/\b(\d{2,3}\/\d{1,3})\b|\b(\d{1,3}\s?%)/);
  if (match) return match[1] || match[2].replace(/\s/g, '');
  return null;
}

// KEPT in the column model for the phase-2 consumer share/email — NOT rendered on the agent
// page (owner feedback #3: "pin it for later addition on the consumer site").
function keyBenefitsOf(plan, limit = 6) {
  const raw = plan._apiData && Array.isArray(plan._apiData.benefits)
    ? plan._apiData.benefits
    : (plan.planDetails && Array.isArray(plan.planDetails.benefits) ? plan.planDetails.benefits : []);
  return raw
    .filter((b) => b && b.name && (b.formattedValue || b.value))
    .slice(0, limit)
    .map((b) => ({ label: String(b.name), value: String(b.formattedValue || b.value) }));
}

const PRODUCT_TYPE_LABELS = {
  'aca': 'ACA Marketplace',
  'stm': 'Short-Term Medical',
  'gap': 'Gap / Expense Shield',
  'major-medical': 'Major Medical',
  'limited-medical': 'Limited Medical',
  'dental': 'Dental',
  'vision': 'Vision',
  'accident': 'Accident',
  'critical-illness': 'Critical Illness',
  'specified-disease': 'Cancer / Specified Disease',
  'hospital-indemnity': 'Hospital Indemnity',
  'term-life': 'Term Life',
  'additional': 'Additional Plan',
  'wellness': 'Wellness',
};

function linksOf(plan) {
  const d = plan.planDetails || {};
  return {
    brochure: d.brochure || plan.pathToBrochure || null,
    sbc: d.summaryUrl || plan.sbcUrl || null,
    // provider directory: carrier-supplied link (NGAH providerLink, CMS network_url)
    provider: d.providerLink || (plan._cmsData && plan._cmsData.network_url) || null,
  };
}

/**
 * Build one comparison column per plan. Callers may merge extras afterwards (script.js
 * overrides `premium` with effectivePremium, attaches `riders` and `coverage`, and refreshes
 * `copays` when the CMS plan detail arrives).
 */
export function buildCompareColumns(plans, opts = {}) {
  const getOop = typeof opts.getOopMax === 'function' ? opts.getOopMax : getOopMax;
  const getTerm = typeof opts.getTermLabel === 'function' ? opts.getTermLabel : defaultTermLabel;

  return (plans || []).filter(Boolean).map((plan) => ({
    planId: String(plan.id),
    carrier: plan.carrier || plan.network || 'LifeX',
    name: plan.name || 'Plan',
    premium: Number(plan.calculatedPremium) || 0,
    riders: [],
    deductible: plan.deductible != null
      ? (parseFloat(String(plan.deductible).replace(/[$,]/g, '')) || 0)
      : null,
    oopMax: getOop(plan),
    coinsurance: coinsuranceOf(plan),
    // Fullest available network name (rev 2 #2): CMS stores only the bare type (HMO/PPO/EPO) —
    // prefix the carrier so the cell reads "Ambetter Health HMO" instead of just "HMO".
    network: (function () {
      const n = plan.network || null;
      if (n && /^(HMO|PPO|EPO|POS|HMO-POS)$/i.test(String(n).trim()) && plan.carrier) {
        return plan.carrier + ' ' + String(n).trim().toUpperCase();
      }
      return n;
    })(),
    term: getTerm(plan),
    metalTier: metalTierOf(plan),
    productTypeLabel: (function () {
      const slug = getProductType(plan);
      if (!slug) return null;
      return PRODUCT_TYPE_LABELS[slug] || String(slug).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    })(),
    links: linksOf(plan),
    copays: extractCopays(plan),
    // Mandatory monthly fees (UHOne FACT) — already inside the premium; disclosed under it.
    fees: (plan.planDetails && Array.isArray(plan.planDetails.monthlyFees)) ? plan.planDetails.monthlyFees : [],
    // MES bundle awareness (brochure AHS_MES_2142-1): each half points at its sibling.
    bundleNote: /medical expense shield - stm/i.test(plan.name || '')
      ? 'STM half of the MES bundle — pair with MES – Fixed Benefit'
      : /medical expense shield - fixed benefit/i.test(plan.name || '')
        ? 'Cash-benefit half of the MES bundle — pairs with MES – Short Term Medical'
        : null,
    coverage: null,           // {doctors:[{name,status}], drugs:[{name,status}]} — attached by script.js
    benefits: keyBenefitsOf(plan),   // retained for phase-2 consumer output; not rendered here
  }));
}

// ---------------------------------------------------------------------------
// Session-scoped snapshot store
// ---------------------------------------------------------------------------

export function saveSelection(selection) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({
      savedAt: Date.now(),
      caseSummary: (selection && selection.caseSummary) || null,
      columns: (selection && selection.columns) || [],
    }));
  } catch { /* restricted storage: compare simply won't carry across pages */ }
}

export function loadSelection() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.columns) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearSelection() {
  try { sessionStorage.removeItem(STORE_KEY); } catch { /* noop */ }
}

export function toggleColumn(columns, column) {
  const list = (columns || []).slice();
  const result = togglePlanId(list.map((c) => c.planId), column.planId);
  if (result.rejected) return { columns: list, added: false, removed: false, rejected: true };
  if (result.removed) {
    return { columns: list.filter((c) => String(c.planId) !== String(column.planId)), added: false, removed: true, rejected: false };
  }
  list.push(column);
  return { columns: list, added: true, removed: false, rejected: false };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function money(value) {
  if (value == null || isNaN(value)) return '—';
  return '$' + Number(value).toLocaleString();
}

/** Aligned attribute rows shared by every column (network renders link-aware in the renderer). */
export const COMPARE_ROWS = [
  { key: 'premium', label: 'Monthly premium', format: (c) => '$' + c.premium.toFixed(2) + '/mo' },
  { key: 'deductible', label: 'Deductible', format: (c) => (c.deductible == null ? 'N/A' : money(c.deductible)) },
  { key: 'oopMax', label: 'Out-of-pocket max', format: (c) => money(c.oopMax) },
  { key: 'coinsurance', label: 'Coinsurance', format: (c) => c.coinsurance || '—' },
  { key: 'network', label: 'Network', format: (c) => c.network || '—' },
  { key: 'term', label: 'Term', format: (c) => c.term || '—' },
  { key: 'metalTier', label: 'Metal tier', format: (c) => c.metalTier || '—' },
  { key: 'productType', label: 'Product type', format: (c) => c.productTypeLabel || '—' },
];

const COVERAGE_GLYPHS = {
  in: '✓ In network', covered: '✓ Covered', generic: '✓ Generic covered',
  out: '✗ Out of network', no: '✗ Not covered', unknown: '—',
};

export function renderCompareTable(columns) {
  if (!columns || columns.length === 0) {
    return '<p class="compare-empty">Select at least two plans to compare.</p>';
  }

  const minPremium = Math.min(...columns.map((c) => Number(c.premium) || 0));

  let html = '<table class="compare-table"><thead><tr><th scope="col" class="compare-table__attr"></th>';
  for (const col of columns) {
    html += '<th scope="col" class="compare-table__plan">' +
      '<button type="button" class="compare-table__remove" data-compare-remove="' + escapeHtml(col.planId) +
      '" aria-label="Remove ' + escapeHtml(col.name) + ' from comparison">&times;</button>' +
      '<span class="compare-table__carrier">' + escapeHtml(col.carrier) + '</span>' +
      '<span class="compare-table__name">' + escapeHtml(col.name) + '</span>' +
      '</th>';
  }
  html += '</tr></thead><tbody>';

  for (const row of COMPARE_ROWS) {
    html += '<tr><th scope="row">' + escapeHtml(row.label) + '</th>';
    for (const col of columns) {
      const best = row.key === 'premium' && (Number(col.premium) || 0) === minPremium && columns.length > 1;
      let cell;
      if (row.key === 'network' && col.network && col.links && col.links.provider) {
        // owner feedback #2: network name links to the carrier's provider directory
        cell = '<a href="' + escapeHtml(col.links.provider) +
          '" target="_blank" rel="noopener noreferrer">' + escapeHtml(col.network) + '</a>';
      } else {
        cell = escapeHtml(row.format(col));
      }
      if (row.key === 'premium' && Array.isArray(col.riders) && col.riders.length) {
        for (const r of col.riders) {
          cell += '<div class="compare-rider">incl. ' + escapeHtml(r.name) +
            ' (+$' + (Number(r.premium) || 0).toFixed(2) + ')</div>';
        }
      }
      if (row.key === 'premium' && Array.isArray(col.fees) && col.fees.length) {
        for (const f of col.fees) {
          cell += '<div class="compare-rider">incl. ' + escapeHtml(f.name) +
            ' fee (+$' + (Number(f.amount) || 0).toFixed(2) + '/mo)</div>';
        }
      }
      if (row.key === 'premium' && col.bundleNote) {
        cell += '<div class="compare-rider">' + escapeHtml(col.bundleNote) + '</div>';
      }
      html += '<td' + (best ? ' class="is-best"' : '') + '>' + cell + '</td>';
    }
    html += '</tr>';
  }

  // Copays & cost sharing — fixed 11 rows (owner feedback #4)
  html += '<tr class="compare-table__section"><th scope="row" colspan="' + (columns.length + 1) + '">Copays &amp; cost sharing</th></tr>';
  for (const cat of COPAY_CATEGORIES) {
    html += '<tr><th scope="row">' + escapeHtml(cat.label) + '</th>';
    for (const col of columns) {
      const v = (col.copays && col.copays[cat.key]) || '—';
      const muted = v === NETWORK_DISCOUNT || v === 'Excluded';
      html += '<td' + (muted ? ' class="is-muted"' : '') + '>' + escapeHtml(v) + '</td>';
    }
    html += '</tr>';
  }

  // Your doctors / prescriptions — only when captured (owner feedback #6)
  const covSource = columns.find((c) => c.coverage && ((c.coverage.doctors || []).length || (c.coverage.drugs || []).length));
  if (covSource) {
    const doctorNames = (covSource.coverage.doctors || []).map((d) => d.name);
    const drugNames = (covSource.coverage.drugs || []).map((d) => d.name);
    if (doctorNames.length) {
      html += '<tr class="compare-table__section"><th scope="row" colspan="' + (columns.length + 1) + '">Your doctors</th></tr>';
      doctorNames.forEach((name, i) => {
        html += '<tr><th scope="row">' + escapeHtml(name) + '</th>';
        for (const col of columns) {
          const entry = col.coverage && (col.coverage.doctors || [])[i];
          html += '<td>' + escapeHtml(COVERAGE_GLYPHS[entry && entry.status] || '—') + '</td>';
        }
        html += '</tr>';
      });
    }
    if (drugNames.length) {
      html += '<tr class="compare-table__section"><th scope="row" colspan="' + (columns.length + 1) + '">Your prescriptions</th></tr>';
      drugNames.forEach((name, i) => {
        html += '<tr><th scope="row">' + escapeHtml(name) + '</th>';
        for (const col of columns) {
          const entry = col.coverage && (col.coverage.drugs || [])[i];
          html += '<td>' + escapeHtml(COVERAGE_GLYPHS[entry && entry.status] || '—') + '</td>';
        }
        html += '</tr>';
      });
    }
  }

  // Documents — brochure only (owner feedback #5)
  const hasBrochure = columns.some((c) => c.links && c.links.brochure);
  if (hasBrochure) {
    html += '<tr class="compare-table__links-row"><th scope="row">Documents</th>';
    for (const col of columns) {
      const b = col.links && col.links.brochure;
      html += '<td>' + (b
        ? '<a href="' + escapeHtml(b) + '" target="_blank" rel="noopener noreferrer">Brochure</a>'
        : '—') + '</td>';
    }
    html += '</tr>';
  }

  html += '</tbody></table>';
  return html;
}

// ---------------------------------------------------------------------------
// Comparison email (spec item 8) — POSTs the live snapshot to quote-send's
// comparison mode. Auth/identity plumbing lives here so the compare page
// script stays DOM-only.
// ---------------------------------------------------------------------------

export async function sendComparisonEmail({ recipientEmail, subject } = {}) {
  const sel = loadSelection();
  if (!sel || !Array.isArray(sel.columns) || sel.columns.length === 0) {
    throw new Error('Nothing to compare — add plans on the results page first.');
  }
  if (!recipientEmail || !String(recipientEmail).trim()) {
    throw new Error('Recipient email is required.');
  }
  const npn = getAgentNpn();
  if (!npn) {
    throw new Error('Register or sign in before emailing a comparison.');
  }
  const profile = getCachedProfile();
  const payload = {
    mode: 'comparison',
    agentNpn: npn,
    recipientEmail: String(recipientEmail).trim(),
    ...(subject ? { subject } : {}),
    ...(profile ? { agentBranding: toBranding(profile) } : {}),
    comparison: { caseSummary: sel.caseSummary || null, columns: sel.columns },
  };
  const response = await fetch('/quotes/send', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || ('Send failed: ' + response.status));
  }
  const parsed = await response.json().catch(() => ({}));
  return parsed.data || {};
}

export const Compare = {
  MAX_COMPARE,
  COMPARE_ROWS,
  COPAY_CATEGORIES,
  NETWORK_DISCOUNT,
  togglePlanId,
  buildCompareColumns,
  extractCopays,
  renderCompareTable,
  saveSelection,
  loadSelection,
  clearSelection,
  toggleColumn,
  sendComparisonEmail,
};
